import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

const API_BASE = import.meta.env.VITE_API_BASE || "http://localhost:5000";

const COUNTRIES = [
  "Tanzania","Kenya","Uganda","Rwanda","Burundi","Ethiopia","Somalia",
  "United Kingdom","United States","Germany","France","Italy","Spain",
  "Netherlands","Belgium","Switzerland","Australia","Canada","India",
  "China","Japan","South Africa","Egypt","Nigeria","Ghana","Senegal",
];

const HOURS   = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = ["00", "15", "30", "45"];

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Montserrat:wght@300;400;500;600;700&display=swap');

  :root {
    --black:#0a0a0a; --deep:#111111; --card:#161616;
    --border:#2a2a2a; --gold:#c9a84c; --gold-light:#e2c36e;
    --white:#f5f0e8; --grey:#888888; --grey-light:#cccccc;
    --green:#25D366; --red:#e05252;
  }
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  body { background:var(--black); color:var(--white); font-family:'Montserrat',sans-serif; font-weight:300; }

  @keyframes fadeUp   { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
  @keyframes shimmer  { 0%{background-position:-200% center} 100%{background-position:200% center} }
  @keyframes spin     { to{transform:rotate(360deg)} }

  /* ── PAGE HEADER ── */
  .det-header {
    background:linear-gradient(160deg,#0d0d0d 0%,#111 60%,#0a0a0a 100%);
    border-bottom:1px solid var(--border);
    padding:100px 60px 36px; position:relative; overflow:hidden;
  }
  .det-header::before {
    content:''; position:absolute; inset:0;
    background:radial-gradient(ellipse 50% 100% at 80% 50%,rgba(201,168,76,0.07) 0%,transparent 70%);
    pointer-events:none;
  }
  .det-breadcrumb {
    font-size:0.62rem; letter-spacing:0.2em; text-transform:uppercase;
    color:var(--grey); margin-bottom:14px;
  }
  .det-breadcrumb span { color:var(--gold); }
  .det-title {
    font-family:'Cormorant Garamond',serif;
    font-size:clamp(1.8rem,3vw,2.8rem); font-weight:300; color:var(--white);
  }
  .det-title em { font-style:italic; color:var(--gold); }

  /* ── BODY LAYOUT ── */
  .det-body {
    display:grid; grid-template-columns:1fr 340px;
    gap:24px; padding:40px 60px 80px; align-items:start;
  }

  /* ── FORM CARD ── */
  .form-card {
    background:var(--card); border:1px solid var(--border);
    position:relative; animation:fadeUp 0.6s ease both;
  }
  .form-card::before {
    content:''; position:absolute; top:0; left:0; right:0; height:2px;
    background:linear-gradient(90deg,transparent,var(--gold),transparent);
    background-size:200% 100%; animation:shimmer 3s ease infinite;
  }
  .form-section {
    padding:32px 32px 24px; border-bottom:1px solid var(--border);
  }
  .form-section:last-of-type { border-bottom:none; }
  .form-section-title {
    font-size:0.6rem; letter-spacing:0.28em; text-transform:uppercase;
    color:var(--gold); margin-bottom:22px;
  }
  .form-grid { display:grid; gap:16px; }
  .form-grid-3 { grid-template-columns:1fr 2fr 2fr; }
  .form-grid-3b { grid-template-columns:1fr 1fr 1fr; }
  .form-grid-2 { grid-template-columns:1fr 1fr; }
  .form-grid-2b { grid-template-columns:1fr 2fr; }
  .form-grid-1 { grid-template-columns:1fr; }

  .field { display:flex; flex-direction:column; gap:6px; }
  .field label {
    font-size:0.60rem; font-weight:bold; letter-spacing:0.2em; text-transform:uppercase; color:#fff;
  }
  .field input, .field select, .field textarea {
    background:#1a1a1a; border:1px solid var(--border);
    color:var(--white); font-family:'Montserrat',sans-serif;
    font-size:0.78rem; padding:10px 14px; outline:none;
    transition:border-color 0.25s, box-shadow 0.25s;
    -webkit-appearance:none; appearance:none;
  }
  .field select {
    background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23c9a84c'/%3E%3C/svg%3E");
    background-repeat:no-repeat; background-position:right 14px center;
    padding-right:36px; cursor:pointer;
  }
  .field textarea { resize:vertical; min-height:90px; line-height:1.6; }
  .field input::placeholder, .field textarea::placeholder { color:#444; }
  .field input:focus, .field select:focus, .field textarea:focus {
    border-color:var(--gold); box-shadow:0 0 0 2px rgba(201,168,76,0.1);
  }
  .field input.error, .field select.error { border-color:var(--red); }

  /* flight time row */
  .time-row { display:flex; align-items:center; gap:8px; }
  .time-sep { color:var(--grey); font-size:1rem; }
  .time-row select { flex:1; }

  /* flight note */
  .flight-note {
    font-size:0.62rem; color:var(--gold); line-height:1.6;
    padding:10px 14px; border:1px solid rgba(201,168,76,0.2);
    background:rgba(201,168,76,0.04); margin-top:4px;
  }

  /* terms */
  .terms-row {
    display:flex; align-items:flex-start; gap:12px;
    padding:24px 32px; border-top:1px solid var(--border);
  }
  .terms-row input[type="checkbox"] {
    width:16px; height:16px; margin-top:2px; cursor:pointer;
    accent-color:var(--gold); flex-shrink:0;
  }
  .terms-row label {
    font-size:0.72rem; color:var(--grey); line-height:1.7; cursor:pointer;
  }
  .terms-row label a { color:var(--gold); text-decoration:none; }
  .terms-row label a:hover { text-decoration:underline; }

  /* submit */
  .submit-row { padding:0 32px 32px; }
  .submit-btn {
    width:100%; padding:14px; background:var(--gold); border:none;
    color:var(--black); font-family:'Montserrat',sans-serif;
    font-size:0.72rem; font-weight:700; letter-spacing:0.25em;
    text-transform:uppercase; cursor:pointer; transition:all 0.3s;
    position:relative; overflow:hidden;
  }
  .submit-btn:hover { background:var(--gold-light); letter-spacing:0.3em; }
  .submit-btn:disabled { background:#2a2a2a; color:#555; cursor:not-allowed; letter-spacing:0.25em; }

  /* ── SIDEBAR ── */
  .det-sidebar { display:flex; flex-direction:column; gap:16px; }

  .side-card {
    background:var(--card); border:1px solid var(--border);
    position:relative;
  }
  .side-card::before {
    content:''; position:absolute; top:0; left:0; right:0; height:2px;
    background:linear-gradient(90deg,transparent,var(--gold),transparent);
    background-size:200% 100%; animation:shimmer 3s ease infinite;
  }
  .side-card-head {
    padding:16px 20px; border-bottom:1px solid var(--border);
    font-size:0.6rem; letter-spacing:0.25em; text-transform:uppercase; color:var(--gold);
  }

  /* rate chart */
  .rate-table { width:100%; border-collapse:collapse; }
  .rate-table th {
    font-size:0.58rem; letter-spacing:0.15em; text-transform:uppercase;
    color:var(--grey); padding:10px 16px; text-align:left;
    border-bottom:1px solid var(--border); background:rgba(0,0,0,0.2);
  }
  .rate-table td {
    font-size:0.72rem; color:var(--white); padding:10px 16px;
    border-bottom:1px solid var(--border);
  }
  .rate-table tr:last-child td { border-bottom:none; }
  .rate-table .td-gold {
    // font-family:'Cormorant Garamond',serif;
    //  font-size:1.1rem;
    font-size:0.8rem;
    color:var(--gold); font-weight:400;
  }
  .rate-table .td-grey { color:var(--grey); font-size:0.68rem; }
  .rate-note {
    padding:12px 16px; font-size:0.62rem; color:var(--gold);
    background:rgba(201,168,76,0.05); border-top:1px solid rgba(201,168,76,0.15);
    line-height:1.65;
  }

  /* itinerary */
  .itin-table { width:100%; border-collapse:collapse; }
  .itin-table td {
    padding:9px 16px; font-size:0.72rem;
    border-bottom:1px solid var(--border);
  }
  .itin-table tr:last-child td { border-bottom:none; }
  .itin-table .itin-lbl { color:var(--grey); width:45%; }
  .itin-table .itin-val { color:var(--white); font-weight:500; }
  .itin-dist {
    padding:12px 16px; 
    // font-size:0.65rem;
    font-size:0.8rem;
     letter-spacing:0.1em;
    color:var(--gold); border-bottom:1px solid var(--border);
    background:rgba(201,168,76,0.04);
  }
  .modify-row {
    padding:14px 16px; display:flex; justify-content:flex-end;
  }
  .modify-btn {
    padding:8px 20px; background:transparent; border:1px solid var(--border);
    color:var(--grey-light); font-family:'Montserrat',sans-serif;
    font-size:0.62rem; letter-spacing:0.15em; text-transform:uppercase;
    cursor:pointer; transition:all 0.25s;
  }
  .modify-btn:hover { border-color:var(--gold); color:var(--gold); }

  /* ── SELECTED CAR STRIP ── */
  .car-strip {
    margin:0 60px 60px;
    background:var(--card); border:1px solid var(--border);
    display:grid; grid-template-columns:180px 1fr auto;
    position:relative; overflow:hidden;
    animation:fadeUp 0.7s ease 0.2s both;
  }
  .car-strip::before {
    content:''; position:absolute; top:0; left:0; right:0; height:2px;
    background:linear-gradient(90deg,transparent,var(--gold),transparent);
    background-size:200% 100%; animation:shimmer 3s ease infinite;
  }
  .strip-img { height:120px; overflow:hidden; background:#111; }
  .strip-img img {
    width:100%; height:100%; object-fit:contain; padding:10px;
    filter:brightness(0.9);
  }
  .strip-info {
    padding:20px 24px; border-left:1px solid var(--border);
    display:flex; flex-direction:column; justify-content:center; gap:8px;
  }
  .strip-car-name {
    font-family:'Cormorant Garamond',serif; font-size:1.3rem;
    color:var(--white); font-weight:400;
  }
  .strip-specs { display:flex; gap:20px; }
  .strip-spec { font-size:0.68rem; color:var(--grey); display:flex; align-items:center; gap:6px; }
  .strip-price-col {
    padding:20px 24px; border-left:1px solid var(--border);
    display:flex; flex-direction:column; align-items:center;
    justify-content:center; gap:8px; min-width:160px;
    background:rgba(0,0,0,0.2);
  }
  .strip-badge {
    font-size:0.55rem; letter-spacing:0.2em; text-transform:uppercase;
    padding:3px 10px; border:1px solid rgba(201,168,76,0.3);
    background:rgba(201,168,76,0.07); color:var(--gold);
  }
  .strip-price {
    font-family:'Cormorant Garamond',serif; font-size:1.8rem;
    color:var(--gold); font-weight:300; line-height:1;
  }
  .strip-modify {
    padding:8px 18px; background:transparent; border:1px solid var(--border);
    color:var(--grey); font-family:'Montserrat',sans-serif;
    font-size:0.62rem; letter-spacing:0.15em; text-transform:uppercase;
    cursor:pointer; transition:all 0.25s;
  }
  .strip-modify:hover { border-color:var(--gold); color:var(--gold); }

  /* ── RESPONSIVE ── */
  @media (max-width:1000px) {
    .det-body { grid-template-columns:1fr; padding:30px; }
    .det-header { padding:100px 30px 30px; }
    .car-strip { margin:0 30px 40px; grid-template-columns:140px 1fr; }
    .strip-price-col { grid-column:1/-1; border-left:none; border-top:1px solid var(--border); flex-direction:row; justify-content:space-between; }
    .form-grid-3 { grid-template-columns:1fr 1fr; }
    .form-grid-3b { grid-template-columns:1fr 1fr; }
  }
  @media (max-width:600px) {
    .det-header { padding:90px 20px 24px; }
    .det-body { padding:20px; }
    .car-strip { margin:0 20px 30px; grid-template-columns:1fr; }
    .form-grid-3,.form-grid-3b,.form-grid-2,.form-grid-2b { grid-template-columns:1fr; }
    .form-section { padding:24px 20px 16px; }
    .terms-row,.submit-row { padding-left:20px; padding-right:20px; }
  }
`;

export default function TransferBookingDetails() {
  const { state }  = useLocation();
  const navigate   = useNavigate();

  const car     = state?.car     || {};
  const finalKm = parseFloat(state?.finalKm) || 0;
  const oneway  = state?.oneway  || "1";

  // price calc — same formula as confirmation page
  const basePrice      = parseFloat(car.base_price)    || 0;
  const ratePerKm      = parseFloat(car.trnsfer_price) || 0;
  const totalKmPrice   = parseFloat((finalKm * ratePerKm).toFixed(2));
  const estimatedTotal = parseFloat((basePrice + totalKmPrice).toFixed(2));
  const roundPrice     = Math.round(estimatedTotal);
  const advanceToPay   = parseFloat((roundPrice / 2).toFixed(2));
  const currency       = "$";

  // ── form state ──
  // const [form, setForm] = useState({
  //   title:"Mr", firstName:"", lastName:"",
  //   address:"", city:"", postcode:"", country:"",
  //   mobile:"", email:"", details:"",
  //   arrFlight:"", arrHour:"00", arrMin:"00",
  //   depFlight:"", depHour:"00", depMin:"00",
  //   flightDetails:"",
  // });

  const [form, setForm] = useState({
    title:        state?.title        || "Mr",
    firstName:    state?.firstName    || "",
    lastName:     state?.lastName     || "",
    address:      state?.address      || "",
    city:         state?.city         || "",
    postcode:     state?.postcode     || "",
    country:      state?.country      || "",
    mobile:       state?.mobile       || "",
    email:        state?.email        || "",
    details:      state?.details      || "",
    arrFlight:    state?.arrFlight    || "",
    arrHour:      state?.arrHour      || "00",
    arrMin:       state?.arrMin       || "00",
    depFlight:    state?.depFlight    || "",
    depHour:      state?.depHour      || "00",
    depMin:       state?.depMin       || "00",
    flightDetails:state?.flightDetails|| "",
  });

  const [agreed,     setAgreed]     = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors,     setErrors]     = useState({});

  // ── inject CSS ──
  useEffect(() => {
    const id = "det-styles";
    if (!document.getElementById(id)) {
      const s = document.createElement("style");
      s.id = id; s.textContent = CSS;
      document.head.appendChild(s);
    }
    return () => document.getElementById(id)?.remove();
  }, []);

  if (!state) { navigate("/"); return null; }

  // ── price calc ──
  // const basePrice      = parseFloat(car.base_price)    || 0;
  // const ratePerKm      = parseFloat(car.trnsfer_price) || 0;
  // const totalKmPrice   = parseFloat((finalKm * ratePerKm).toFixed(2));
  // const estimatedTotal = parseFloat((basePrice + totalKmPrice).toFixed(2));
  // const roundPrice     = Math.round(estimatedTotal);
  // const advanceToPay   = parseFloat((roundPrice / 2).toFixed(2));
  // const currency       = "$";
  // price calc moved above

  // ── field change ──
  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(e => ({ ...e, [k]: false }));
  };

  // ── validation ──
  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = true;
    if (!form.lastName.trim())  e.lastName  = true;
    if (!form.mobile.trim())    e.mobile    = true;
    if (!form.email.trim())     e.email     = true;
    if (!form.country)          e.country   = true;
    if (!agreed)                e.terms     = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── submit ──
  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        // trip info
        pickupLocation: state.pickupLocation,
        dropLocation:   state.dropLocation,
        pickupDate:     state.pickupDate,
        pickupTime:     state.pickupTime,
        returnDate:     state.returnDate,
        returnTime:     state.returnTime,
        passengers:     state.passengers,
        oneway,
        // vehicle + pricing — always carried forward
        car,
        finalKm,
        car_id:      car.car_id,
        total_price: estimatedTotal,
        // personal details
        ...form,
      };
      
      const res  = await fetch(`${API_BASE}/api/transfer/booking`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        navigate("/transfer-step4", {
          state: { ...payload, bookingRef: data.bookingRef }
        });
      } else {
        alert(data.message || "Booking failed. Please try again.");
      }
    } catch {
      // // API not ready — go to confirmation with mock ref
      // navigate("/transfer-step4", {
      //   state: { ...state, ...form, finalKm, total_price: estimatedTotal, bookingRef: "TRF-MOCK-001" }
      // });
      // API not ready — go to confirmation with mock ref
      navigate("/transfer-step4", {
        state: { ...state, ...form, car, finalKm, total_price: estimatedTotal, bookingRef: "TRF-MOCK-001" }
      });
    } finally {
      setSubmitting(false);
    }
  };

  // const F = ({ label, name, type="text", placeholder="", as="input", children, span }) => (
  //   <div className={`field${span ? ` grid-span-${span}` : ""}`}>
  //     <label>{label}</label>
  //     {as === "select" ? (
  //       <select
  //         className={errors[name] ? "error" : ""}
  //         value={form[name]}
  //         onChange={e => set(name, e.target.value)}
  //       >{children}</select>
  //     ) : as === "textarea" ? (
  //       <textarea
  //         placeholder={placeholder}
  //         value={form[name]}
  //         onChange={e => set(name, e.target.value)}
  //       />
  //     ) : (
  //       <input
  //         type={type}
  //         placeholder={placeholder}
  //         className={errors[name] ? "error" : ""}
  //         value={form[name]}
  //         onChange={e => set(name, e.target.value)}
  //       />
  //     )}
  //   </div>
  // );
  // F component removed — replaced with direct JSX fields below

  return (
    <div>
      <Navbar activePage="Book a Taxi" />

      {/* ── Header ── */}
      <div className="det-header">
        <div className="det-breadcrumb">
          Home <span>›</span> Book a Taxi <span>›</span> Available Vehicles <span>›</span> Passenger Details
        </div>
        <h1 className="det-title">Passenger <em>Details</em></h1>
      </div>

      {/* ── Body ── */}
      <div className="det-body">

        {/* ── LEFT: Form ── */}
        <div className="form-card">

          {/* Personal Details */}
          <div className="form-section">
            <div className="form-section-title">Personal Information</div>
            <div className="form-grid form-grid-3" style={{ marginBottom: 16 }}>
              <div className="field">
                <label>Title</label>
                <select value={form.title} onChange={e => set("title", e.target.value)}>
                  {["Mr","Mrs","Ms","Dr","Prof"].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="field">
                <label>First Name</label>
                <input type="text" placeholder="Enter your first name"
                  className={errors.firstName ? "error" : ""}
                  value={form.firstName} onChange={e => set("firstName", e.target.value)} />
              </div>
              <div className="field">
                <label>Last Name</label>
                <input type="text" placeholder="Enter your last name"
                  className={errors.lastName ? "error" : ""}
                  value={form.lastName} onChange={e => set("lastName", e.target.value)} />
              </div>
            </div>

            <div className="form-grid form-grid-3b" style={{ marginBottom: 16 }}>
              <div className="field">
                <label>Address</label>
                <input type="text" placeholder="Enter address"
                  value={form.address} onChange={e => set("address", e.target.value)} />
              </div>
              <div className="field">
                <label>City</label>
                <input type="text" placeholder="Enter city"
                  value={form.city} onChange={e => set("city", e.target.value)} />
              </div>
              <div className="field">
                <label>Post Code</label>
                <input type="text" placeholder="Enter postcode"
                  value={form.postcode} onChange={e => set("postcode", e.target.value)} />
              </div>
            </div>

            <div className="form-grid form-grid-2b" style={{ marginBottom: 16 }}>
              <div className="field">
                <label>Country</label>
                <select className={errors.country ? "error" : ""}
                  value={form.country} onChange={e => set("country", e.target.value)}>
                  <option value="">Select Country</option>
                  {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Mobile No.</label>
                <input type="tel" placeholder="Enter mobile number"
                  className={errors.mobile ? "error" : ""}
                  value={form.mobile} onChange={e => set("mobile", e.target.value)} />
              </div>
            </div>

            <div className="form-grid form-grid-2">
              <div className="field">
                <label>Email ID</label>
                <input type="email" placeholder="Enter your email"
                  className={errors.email ? "error" : ""}
                  value={form.email} onChange={e => set("email", e.target.value)} />
              </div>
              <div className="field">
                <label>Detail Information</label>
                <textarea placeholder="Put pickup and drop off details"
                  value={form.details} onChange={e => set("details", e.target.value)} />
              </div>
            </div>
          </div>

          {/* Flight Details */}
          <div className="form-section">
            <div className="form-section-title">Flight Information</div>
            <div className="form-grid form-grid-3b" style={{ marginBottom: 16 }}>

              {/* Arrival */}
              <div className="field">
                <label>Arrival Flight No.</label>
                <input
                  type="text" placeholder="Flight No."
                  value={form.arrFlight}
                  onChange={e => set("arrFlight", e.target.value)}
                />
                <label style={{ marginTop: 10 }}>Landing Time</label>
                <div className="time-row">
                  <select value={form.arrHour} onChange={e => set("arrHour", e.target.value)}>
                    {HOURS.map(h => <option key={h}>{h}</option>)}
                  </select>
                  <span className="time-sep">:</span>
                  <select value={form.arrMin} onChange={e => set("arrMin", e.target.value)}>
                    {MINUTES.map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
              </div>

              {/* Departure */}
              <div className="field">
                <label>Departure Flight No.</label>
                <input
                  type="text" placeholder="Flight No."
                  value={form.depFlight}
                  onChange={e => set("depFlight", e.target.value)}
                />
                <label style={{ marginTop: 10 }}>Take Off Time</label>
                <div className="time-row">
                  <select value={form.depHour} onChange={e => set("depHour", e.target.value)}>
                    {HOURS.map(h => <option key={h}>{h}</option>)}
                  </select>
                  <span className="time-sep">:</span>
                  <select value={form.depMin} onChange={e => set("depMin", e.target.value)}>
                    {MINUTES.map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
              </div>

              {/* Flight notes */}
              <div className="field">
                <label>Flight Details</label>
                <textarea
                  placeholder="Additional flight details..."
                  value={form.flightDetails}
                  onChange={e => set("flightDetails", e.target.value)}
                  style={{ minHeight: 110 }}
                />
                <div className="flight-note">
                  ** Flight No and Landing Time is only applicable for Airport pickup and drop off
                </div>
              </div>

            </div>
          </div>

          {/* Terms */}
          <div className="terms-row">
            <input
              type="checkbox" id="terms"
              checked={agreed}
              onChange={e => { setAgreed(e.target.checked); if (errors.terms) setErrors(er => ({ ...er, terms: false })); }}
              style={{ outline: errors.terms ? "1px solid var(--red)" : "none" }}
            />
            <label htmlFor="terms">
              I confirm that I have read, understood and agree with the{" "}
              <a href="#">Terms and Conditions</a> provided.
            </label>
          </div>

          {errors.terms && (
            <div style={{ padding: "0 32px 12px", fontSize: "0.65rem", color: "var(--red)" }}>
              Please accept the terms and conditions.
            </div>
          )}

          {/* Submit */}
          <div className="submit-row">
            <button className="submit-btn" onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Submitting..." : "Submit Booking »"}
            </button>
          </div>

        </div>

        {/* ── RIGHT: Sidebar ── */}
        <div className="det-sidebar">

          {/* Rate Chart */}
          <div className="side-card">
            <div className="side-card-head">Rate Chart</div>
            <table className="rate-table">
              <thead>
                <tr>
                  <th>Quote</th>
                  <th>Calculation</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Base Rate</td>
                  <td className="td-grey">—</td>
                  <td className="td-gold">{currency}{basePrice.toFixed(2)}</td>
                </tr>
                <tr>
                  <td>Per KM Price</td>
                  <td className="td-grey">{currency}{ratePerKm.toFixed(2)}</td>
                  <td className="td-grey">—</td>
                </tr>
                <tr>
                  <td>Total KM</td>
                  <td className="td-grey">{finalKm} km</td>
                  <td className="td-grey">—</td>
                </tr>
                <tr>
                  <td>Total KM Price</td>
                  <td className="td-grey">{finalKm} × {currency}{ratePerKm.toFixed(2)}</td>
                  <td className="td-gold">{currency}{totalKmPrice.toFixed(2)}</td>
                </tr>
                <tr>
                  <td>Estimated Amount</td>
                  <td className="td-grey">—</td>
                  <td className="td-gold">{currency}{estimatedTotal.toFixed(2)}</td>
                </tr>
                <tr>
                  <td>Round Price</td>
                  <td className="td-grey">—</td>
                  <td className="td-gold">{currency}{roundPrice.toFixed(2)}</td>
                </tr>
                <tr>
                  <td><strong>Advance to Pay</strong></td>
                  <td className="td-grey">50%</td>
                  <td className="td-gold" style={{ fontSize: "1.2rem" }}>{currency}{advanceToPay.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
            <div className="rate-note">
              <strong>Note:</strong> Your rate was calculated based on the information provided. Some modifications may change this rate.
            </div>
          </div>

          {/* Itinerary */}
          <div className="side-card">
            <div className="side-card-head">Itinerary Details</div>
            <div className="itin-dist">Total Distance: {finalKm} KM</div>
            <table className="itin-table">
              <tbody>
                {[
                  { label: "Pick Up Location", value: state.pickupLocation },
                  { label: "Pick-up Date",     value: state.pickupDate },
                  { label: "Pick-up Time",     value: state.pickupTime },
                  { label: "Drop Location",    value: state.dropLocation },
                  { label: "Passengers",       value: state.passengers },
                  ...(state.returnDate ? [
                    { label: "Return Date", value: state.returnDate },
                    { label: "Return Time", value: state.returnTime },
                  ] : []),
                ].map(r => (
                  <tr key={r.label}>
                    <td className="itin-lbl">{r.label}</td>
                    <td className="itin-val">{r.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="modify-row">
              {/* <button className="modify-btn" onClick={() => navigate(-1)}>← Modify</button> */}
              <button className="modify-btn" onClick={() => navigate("/transfer", {
          state: state })} > ← Modify </button>
            </div>
          </div>

        </div>
      </div>

      {/* ── Selected Car Strip ── */}
      <div className="car-strip">
        <div className="strip-img">
          <img
            src={`${API_BASE}/${car.car_image}`}
            alt={car.car_name}
            onError={e => { e.target.style.display = "none"; }}
          />
        </div>
        <div className="strip-info">
          <div className="strip-car-name">{car.car_name}</div>
          <div className="strip-specs">
            <span className="strip-spec">👥 {car.passengers} Passengers</span>
            <span className="strip-spec">🧳 {car.luggage} Luggage</span>
          </div>
        </div>
        <div className="strip-price-col">
          <span className="strip-badge">{oneway === "1" ? "One Way" : "Two Way"}</span>
          <div className="strip-price">{currency}{estimatedTotal.toFixed(2)}</div>
          <button className="strip-modify" onClick={() => navigate(-1)}>← Modify</button>
        </div>
      </div>

    </div>
  );
}